import { client, extractErrorMessage } from 'shared';
import { UpperDaysOfWeek } from 'shared/Dates';
import { JobDetailValue } from 'models';
import type { IPayScale } from 'parent-portal/jobs/postJob/payScales/types';
import { mergeTierQualifications } from './payScaleOptions';
import type { FaqAnswer, RequestedWorker, ShiftTimes, TimesMode } from './formTypes';

export interface JobRequestInput {
    familyId: number;
    addressId?: number;
    businessLocationId?: number;
    startDate: string;
    endDate: string;
    selectedDays: UpperDaysOfWeek[];
    timesMode: TimesMode;
    times: ShiftTimes;
    perDayTimes: Record<UpperDaysOfWeek, ShiftTimes>;
    includeBreak: boolean;
    breakLength: string;
    headline: string;
    comments: string;
    detailValues: JobDetailValue[];
    payScales: IPayScale[];
    requestedWorkers: RequestedWorker[];
    faqs: FaqAnswer[];
    parentRequestId: number | null;
    isVpkClassroom: boolean;
}

export function buildPayScalesPatch(payScales: IPayScale[], isVpkClassroom: boolean) {
    const [baseScale, ...tierScales] = payScales;
    const mergedScales = [
        baseScale,
        ...tierScales.map((tier) => ({
            ...tier,
            qualifications: mergeTierQualifications(baseScale.qualifications, tier.qualifications),
        })),
    ];
    const rates = mergedScales.map((scale) => scale.rate).filter((rate) => Number.isFinite(rate));
    const payMin = Math.min(...rates);
    const payMax = Math.max(...rates);
    const payIsRange = payMin !== payMax;

    return {
        pay: payIsRange ? null : payMin,
        rate_min: payIsRange ? payMin : null,
        rate_max: payIsRange ? payMax : null,
        pay_scales: mergedScales.map((scale) => ({
            rate: scale.rate,
            description: scale.description || String(scale.rate),
            qualifications: scale.qualifications,
        })),
        is_vpk_classroom: isVpkClassroom,
    };
}

export function buildFaqsPayload(faqs: FaqAnswer[]) {
    return faqs
        .filter((faq) => faq.answer.trim() !== '')
        .map((faq) => ({ question: { id: faq.questionId }, answer: faq.answer }));
}

export function buildJobDetailsPayload(detailValues: JobDetailValue[]) {
    return detailValues.map((value) => ({ ...value, option: value.option_id }));
}

export function buildTimesOfDay(
    selectedDays: UpperDaysOfWeek[],
    timesMode: TimesMode,
    times: ShiftTimes,
    perDayTimes: Record<UpperDaysOfWeek, ShiftTimes>,
) {
    return selectedDays.map((day) => ({ [day]: timesMode === 'same' ? times : perDayTimes[day] }));
}

export function jobRequestErrorMessage(error: unknown, fallback: string): string {
    const paymentMessage = (error as { payment?: { message?: string } })?.payment?.message;
    if (paymentMessage) return paymentMessage;
    const errors = (error as { errors?: string[] })?.errors;
    if (errors?.length) return errors.join(' ');
    return extractErrorMessage(error, fallback);
}

export function buildJobRequest(input: JobRequestInput, status: 'DRAFT' | 'ACTIVE') {
    return {
        family: input.familyId,
        status,
        start_date: input.startDate || null,
        end_date: input.endDate || null,
        days_of_week: input.selectedDays,
        times_of_day: buildTimesOfDay(input.selectedDays, input.timesMode, input.times, input.perDayTimes),
        preferred_sitters: {
            preferred_sitters: input.requestedWorkers.map((worker) => ({
                sitter: worker.id,
                spotlight: worker.spotlight,
            })),
        },
        faqs: buildFaqsPayload(input.faqs),
        multiple_providers: true,
        ongoing: true,
        business_job_type: 'SUBSTITUTE',
        pay_type: 'PERCENTAGE',
        pay_window: 'DAILY',
        break_required: input.includeBreak,
        break_length: input.includeBreak ? parseInt(input.breakLength, 10) || null : null,
        headline: input.headline || null,
        family_comments: input.comments,
        job_details: buildJobDetailsPayload(input.detailValues),
        ...buildPayScalesPatch(input.payScales, input.isVpkClassroom),
        address: input.addressId ?? null,
        business_location: input.businessLocationId ?? null,
        ...(input.parentRequestId ? { parent_request: input.parentRequestId } : {}),
    };
}

export function saveJobRequest(body: ReturnType<typeof buildJobRequest>, jobId: number | null) {
    return client(`api/ongoing/${jobId ?? ''}`, {
        method: jobId ? 'PATCH' : 'POST',
        body,
    });
}

export interface ApiPayScale {
    id: number;
    rate: number;
    description: string;
    qualifications: {
        value: string | null;
        option: string | null;
        name: string;
        qualification_id: number;
        option_id: number | null;
    }[];
}

export function fetchLastJobPayScales(): Promise<ApiPayScale[]> {
    return client('api/pay-scale/');
}

export interface OngoingJobResponse {
    id: number;
    status: string;
    start_date: string | null;
    end_date: string | null;
    days_of_week: string[] | null;
    times_of_day: Record<string, { start: string; end: string; slots?: number }>[] | null;
    headline: string | null;
    family_comments: string | null;
    details:
        | { detail: number; option?: { id: number } | null; option_id?: number | null; value?: string | null }[]
        | null;
    break_required: boolean | null;
    break_length: number | null;
    is_vpk_classroom: boolean | null;
    address: number | null;
    preferred_sitters: { preferred_sitters: { sitter: number; spotlight?: boolean }[] } | null;
    requested_workers: { id: number; name: string; spotlight: boolean; pictureUrl: string | null }[] | null;
    faqs: { answer: string; question: { id: number; question: string } }[] | null;
    pay_scales: ApiPayScale[] | null;
}

export function fetchJobRequest(jobId: number): Promise<OngoingJobResponse> {
    return client(`api/ongoing/${jobId}/`);
}

export interface JobTemplateListItem {
    id: number;
    headline: string | null;
    start_date: string | null;
    end_date: string | null;
    subs_booked: number;
    filled: boolean;
}

export function fetchJobTemplates(): Promise<JobTemplateListItem[]> {
    return client('api/job-template/?open_jobs=false');
}
